import {
  AuctionBiddingEngine,
  generateBidIncrements,
} from '@bonhams1793/bonhams-typescript'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { useCallback, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useStore } from '@nx/app-store'
import {
  ChannelState,
  UserChannelLastPayload,
  buildUserChannelName,
  showGenericOutbidToast,
  showOutbidToast,
  useUserChannel,
} from '@nx/bidding-engine'
import { usePusher } from '@nx/external/pusher'
import { buildLotPath } from '@nx/helpers'
import { seoName } from '@nx/seo-name'

import { useModals } from '@web/components/common/ModalManager/ModalContext'
import { getTypesenseLotDetails, useGetUser } from '@web/helpers/api'
import { useGetLaunchDarklyFlags } from '@web/helpers/api/launchDarkly'

const { publicRuntimeConfig } = getConfig()

export const useOutbidNotifications = () => {
  const router = useRouter()
  const { data: user } = useGetUser()
  const { modalState, dispatch } = useModals()
  const { data: flags } = useGetLaunchDarklyFlags()

  const channelName = user?.customerId
    ? buildUserChannelName(user.customerId)
    : ''
  const channel: ChannelState | undefined = useStore(
    useShallow((store) => store.channels[channelName])
  )
  const storeActions = useStore(
    useShallow((store) => ({
      addRegistration: store.addRegistration,
      removeRegistration: store.removeRegistration,
      updateChannelPayload: store.updateChannelPayload,
    }))
  )

  const { subscribe, unsubscribe } = usePusher()

  useUserChannel({
    customerId: user?.customerId,
    subscribe,
    unsubscribe,
    addRegistration: storeActions.addRegistration,
    removeRegistration: storeActions.removeRegistration,
    updateChannelPayload: storeActions.updateChannelPayload,
  })

  const onIncreaseBidCallback = useCallback(
    ({
      auctionId,
      biddingEngine,
      lotId,
      title,
    }: {
      auctionId: string
      biddingEngine: `${AuctionBiddingEngine}`
      lotId: string
      title: string
    }) => {
      if (biddingEngine === 'bonhams_bidding_engine') {
        dispatch?.({
          type: 'OPEN',
          payload: {
            modal: 'BID_NOW',
            props: {
              auctionId: auctionId,
              lotId: lotId,
              lotPageLink: buildLotPath({
                auctionId: auctionId,
                lotId: lotId,
                lotName: title,
                domain: publicRuntimeConfig.WEB_BASE_URL,
              }),
              trackingLocation: 'Outbid Toast',
            },
          },
        })
      } else {
        router.push(
          buildLotPath({
            lotId,
            auctionId,
            lotName: '',
          })
        )
      }
    },
    [dispatch, router]
  )

  const outbidEventHandler = useCallback(
    async (payload: NonNullable<UserChannelLastPayload>) => {
      if (!payload?.auctionId) return

      if (
        modalState.modal === 'BID_NOW' &&
        modalState.props &&
        modalState.props.auctionId === String(payload.auctionId) && // TODO: clean up - even though payload.auctionId is already a string the BE might send a number for a little while so we cast it to a String() to be safe. Clean this up when the BE work is done
        modalState.props.lotId === payload.lotNoFull
      ) {
        return
      }

      const lot = await getTypesenseLotDetails({
        id: `${payload.auctionId}-${payload.lotNoFull}`,
      })

      if (lot) {
        const isEnabled = ![seoName(lot.title), 'mybonhams/invoices'].some(
          (path) => router.asPath.includes(path)
        )

        if (!isEnabled || !payload.auctionId) return
        showOutbidToast({
          lot,
          outbidValue: payload.currentBid,
          nextIncrementValue: generateBidIncrements({
            policy: payload.bidIncrementPolicy,
            currentBid: payload.currentBid,
            amountOfOptions: 1,
          })[0],
          onIncreaseBidClick: () =>
            onIncreaseBidCallback({
              auctionId: lot.auctionId,
              biddingEngine: payload.biddingEngine,
              lotId: payload.lotNoFull,
              title: lot.title,
            }),
        })
      } else {
        showGenericOutbidToast({
          auctionId: String(payload.auctionId), // TODO: clean up - even though payload.auctionId is already a string the BE might send a number for a little while so we cast it to a String() to be safe. Clean this up when the BE work is done
          lotId: payload.lotNoFull,
          onIncreaseBidClick: /* istanbul ignore next */ () =>
            onIncreaseBidCallback({
              auctionId: String(payload.auctionId) as string, // TODO: clean up - even though payload.auctionId is already a string the BE might send a number for a little while so we cast it to a String() to be safe. Clean this up when the BE work is done
              biddingEngine: payload.biddingEngine,
              lotId: payload.lotNoFull,
              title: '',
            }),
        })
      }
    },
    [modalState, onIncreaseBidCallback, router.asPath]
  )

  useEffect(() => {
    if (
      flags?.['pusher-outbid-notifications'] &&
      channel?.lastPayload &&
      channel.lastPayload.type === 'user.outbid'
    ) {
      outbidEventHandler(channel.lastPayload)
      storeActions.updateChannelPayload({ channelName, payload: null })
    }
  }, [channel, flags, channelName, outbidEventHandler, storeActions])
}
